import React, { useState } from 'react';

const DIETARY_RESTRICTIONS = [
  { key: 'restrictionGlutenFree', value: 'Gluten-Free' },
  { key: 'restrictionDairyFree', value: 'Dairy-Free' },
  { key: 'restrictionNutFree', value: 'Nut-Free' },
  { key: 'restrictionVegan', value: 'Vegan' },
  { key: 'restrictionVegetarian', value: 'Vegetarian' },
  { key: 'restrictionHalal', value: 'Halal' },
  { key: 'restrictionKosher', value: 'Kosher' },
  { key: 'restrictionLowSodium', value: 'Low-Sodium' },
  { key: 'restrictionPeanutFree', value: 'Peanut-Free' },
];

const DIET_PREFERENCES = [
  { key: 'dietKeto', value: 'Keto' },
  { key: 'dietLowCarb', value: 'Low-Carb' },
  { key: 'dietHighProtein', value: 'High-Protein' },
  { key: 'dietMediterranean', value: 'Mediterranean' },
  { key: 'dietPaleo', value: 'Paleo' },
  { key: 'dietSugarFree', value: 'Sugar-Free' },
  { key: 'dietPlantBased', value: 'Plant-Based' },
  { key: 'dietBalanced', value: 'Balanced' },
];

const HEALTH_CONDITIONS = [
  { value: 'NONE', labelKey: 'healthNone' },
  { value: 'HIGH_BLOOD_PRESSURE', labelKey: 'healthHighBP' },
  { value: 'DIABETES', labelKey: 'healthDiabetes' },
  { value: 'HIGH_CHOLESTEROL', labelKey: 'healthCholesterol' },
  { value: 'OTHER', labelKey: 'healthOther' },
];

const TOTAL_STEPS = 4;

const toggleArrayValue = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const PreferencesForm = ({ initialValues = {}, onSubmit, onSkip, isSubmitting, labels }) => {
  const t = labels || {};

  const [step, setStep] = useState(1);
  const [isStepSaving, setIsStepSaving] = useState(false);
  const [form, setForm] = useState({
    gender: initialValues.gender || '',
    heightCm: initialValues.heightCm || '',
    heightUnit: initialValues.heightUnit || 'cm',
    weightKg: initialValues.weightKg || '',
    weightUnit: initialValues.weightUnit || 'kg',
    dietaryRestrictions: initialValues.dietaryRestrictions || [],
    dietPreferences: initialValues.dietPreferences || [],
    healthConditions: initialValues.healthConditions || [],
    healthConditionsOther: initialValues.healthConditionsOther || '',
  });

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const buildPayload = () => {
    const payload = {};
    if (form.gender) payload.gender = form.gender;
    if (form.heightCm !== '') payload.heightCm = Number(form.heightCm);
    if (form.heightUnit) payload.heightUnit = form.heightUnit;
    if (form.weightKg !== '') payload.weightKg = Number(form.weightKg);
    if (form.weightUnit) payload.weightUnit = form.weightUnit;
    payload.dietaryRestrictions = form.dietaryRestrictions;
    payload.dietPreferences = form.dietPreferences;
    payload.healthConditions = form.healthConditions;
    if (form.healthConditions.includes('OTHER') && form.healthConditionsOther.trim()) {
      payload.healthConditionsOther = form.healthConditionsOther.trim();
    }
    return payload;
  };

  const saveProgress = async () => {
    setIsStepSaving(true);
    try {
      await onSubmit(buildPayload(), { silent: true });
    } finally {
      setIsStepSaving(false);
    }
  };

  const handleExit = async () => {
    await saveProgress();
    onSkip();
  };

  const handleNext = async () => {
    if (step < TOTAL_STEPS) {
      await saveProgress();
      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    }
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSave = () => onSubmit(buildPayload(), { silent: false });

  const stepLabel = (t.stepOf || 'Step {step} of {total}')
    .replace('{step}', step)
    .replace('{total}', TOTAL_STEPS);

  const stepTitles = [
    t.stepTitle1 || 'Personal Stats',
    t.stepTitle2 || 'Dietary Restrictions',
    t.stepTitle3 || 'Diet Preferences',
    t.stepTitle4 || 'Medical Conditions',
  ];

  const stepSubtitles = [
    t.stepSubtitle1 || 'Gender, height and weight',
    t.stepSubtitle2 || 'Select dietary restrictions',
    t.stepSubtitle3 || 'Choose your preferred eating style',
    t.stepSubtitle4 || 'Tell us about relevant conditions',
  ];

  // --- Step 1: Body metrics ---
  const renderStep1 = () => (
    <div className="pref-step">
      <div className="pref-field">
        <label>{t.genderLabel || 'Gender'}</label>
        <div className="pref-radio-group">
          {[
            { value: 'MALE', label: t.genderMale || 'Male' },
            { value: 'FEMALE', label: t.genderFemale || 'Female' },
            { value: 'OTHER', label: t.genderOther || 'Other' },
          ].map(({ value, label }) => (
            <label key={value} className="pref-radio">
              <input
                type="radio"
                name="gender"
                value={value}
                checked={form.gender === value}
                onChange={() => setField('gender', value)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="pref-field">
        <label>{t.heightLabel || 'Height'}</label>
        <div className="pref-unit-row">
          <input
            type="number"
            min="50" max="300"
            value={form.heightCm}
            onChange={(e) => setField('heightCm', e.target.value)}
            placeholder={form.heightUnit === 'cm' ? 'e.g. 170' : 'e.g. 5.6'}
          />
          <select value={form.heightUnit} onChange={(e) => setField('heightUnit', e.target.value)}>
            <option value="cm">cm</option>
            <option value="ft">ft</option>
          </select>
        </div>
      </div>

      <div className="pref-field">
        <label>{t.weightLabel || 'Weight'}</label>
        <div className="pref-unit-row">
          <input
            type="number"
            min="10" max="500"
            value={form.weightKg}
            onChange={(e) => setField('weightKg', e.target.value)}
            placeholder={form.weightUnit === 'kg' ? 'e.g. 70' : 'e.g. 154'}
          />
          <select value={form.weightUnit} onChange={(e) => setField('weightUnit', e.target.value)}>
            <option value="kg">kg</option>
            <option value="lbs">lbs</option>
          </select>
        </div>
      </div>
    </div>
  );

  // --- Step 2: Dietary restrictions ---
  const renderStep2 = () => (
    <div className="pref-step pref-step-cards">
      <label className="pref-section-label">{t.restrictionsLabel || 'Dietary Restrictions'}</label>
      <div className="pref-card-grid">
        {DIETARY_RESTRICTIONS.map(({ key, value }) => {
          const label = t[key] || value;

          return (
          <button
            key={key}
            type="button"
            className={`pref-card${form.dietaryRestrictions.includes(value) ? ' selected' : ''}`}
            onClick={() => setField('dietaryRestrictions', toggleArrayValue(form.dietaryRestrictions, value))}
          >
            <span className="pref-card-icon">🥗</span>
            <span className="pref-card-copy">
              <strong>{label}</strong>
            </span>
          </button>
          );
        })}
      </div>
    </div>
  );

  // --- Step 3: Diet preferences ---
  const renderStep3 = () => (
    <div className="pref-step pref-step-cards">
      <label className="pref-section-label">{t.dietLabel || 'Diet Preferences'}</label>
      <div className="pref-card-grid">
        {DIET_PREFERENCES.map(({ key, value }) => {
          const label = t[key] || value;

          return (
          <button
            key={key}
            type="button"
            className={`pref-card${form.dietPreferences.includes(value) ? ' selected' : ''}`}
            onClick={() => setField('dietPreferences', toggleArrayValue(form.dietPreferences, value))}
          >
            <span className="pref-card-icon">🍽️</span>
            <span className="pref-card-copy">
              <strong>{label}</strong>
            </span>
          </button>
          );
        })}
      </div>
    </div>
  );

  // --- Step 4: Health conditions ---
  const renderStep4 = () => (
    <div className="pref-step pref-step-cards">
      <label className="pref-section-label">{t.healthLabel || 'Health Conditions'}</label>
      <div className="pref-card-grid">
        {HEALTH_CONDITIONS.map(({ value, labelKey }) => (
          <button
            key={value}
            type="button"
            className={`pref-card${form.healthConditions.includes(value) ? ' selected' : ''}`}
            onClick={() => setField('healthConditions', toggleArrayValue(form.healthConditions, value))}
          >
            <span className="pref-card-icon">🩺</span>
            <span className="pref-card-copy">
              <strong>{t[labelKey] || value}</strong>
            </span>
          </button>
        ))}
      </div>
      {form.healthConditions.includes('OTHER') && (
        <div className="pref-field">
          <label>{t.healthOtherLabel || 'Please describe'}</label>
          <input
            type="text"
            maxLength={200}
            value={form.healthConditionsOther}
            onChange={(e) => setField('healthConditionsOther', e.target.value)}
            placeholder="e.g. celiac disease"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="preferences-form">
      <div className="pref-wizard-header">
        <div>
          <p className="pref-step-label">{stepLabel}</p>
          <h2 className="pref-step-title">{stepTitles[step - 1]}</h2>
          <p className="pref-step-subtitle">{stepSubtitles[step - 1]}</p>
        </div>
      </div>

      <div className="pref-progress" aria-hidden="true">
        {[1, 2, 3, 4].map((s) => (
          <span key={s} className={`pref-progress-segment${s <= step ? ' active' : ''}`} />
        ))}
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      <div className="pref-actions">
        <div className="pref-actions-left">
          {step > 1 ? (
            <button type="button" className="btn-secondary" onClick={handleBack} disabled={isStepSaving || isSubmitting}>
              {t.back || 'Back'}
            </button>
          ) : null}

          {step < TOTAL_STEPS ? (
            <button type="button" className="btn-primary" onClick={handleNext} disabled={isStepSaving || isSubmitting}>
              {isStepSaving ? (t.savingStep || 'Saving…') : (t.next || 'Next')}
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? (t.savingFinal || 'Saving…') : (t.save || 'Save')}
            </button>
          )}
        </div>

        <div className="pref-actions-right">
          <button type="button" className="btn-ghost pref-exit-btn" onClick={handleExit} disabled={isStepSaving || isSubmitting}>
            {t.exitAndSave || 'Exit & Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesForm;
